import useConfig from 'hooks/useConfig';
import React, { useEffect, useState } from 'react';
import {
  GoogleMap, MarkerF, useJsApiLoader,
} from '@react-google-maps/api';
import {
  Box, Button, Skeleton, Typography, useTheme,
} from '@mui/material';
import Icon from 'components/Icon';
import { getStaticConfig } from 'utils/config';

function BaseMap({
  children,
  center: defaultCenter,
  height = '100%',
  onLoad: onLoadProp,
  showDirection,
  onClick = () => { },
  defaultZoom = 13,
  ...props
}) {
  const [map, setMap] = useState(null);
  const [center, setCenter] = useState(defaultCenter);
  const googleMapAPIKey = getStaticConfig('pwa.googleMapApiKey', 'AIzaSyCJQt4EfdWfsGYtDUngHd6jP9YWO5ihy4w');
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: googleMapAPIKey,
    libraries: ['places', 'geocoding'],
  });

  useEffect(() => {
    if (map && center?.lat && center?.lng) {
      map.panTo(center);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center?.lat, center?.lng, map]);

  useEffect(() => {
    if (defaultCenter?.lat) {
      setCenter({
        lat: Number(defaultCenter?.lat),
        lng: Number(defaultCenter?.lng),
      });
    }
  }, [defaultCenter?.lat, defaultCenter?.lng]);

  const onLoad = React.useCallback((map) => {
    setMap(map);
    if (onLoadProp) {
      onLoadProp(map);
    }
  }, []);

  const onUnmount = React.useCallback((map) => {
    setMap(null);
  }, []);
  const openInGoogleMaps = () => {
    const url = `https://www.google.com/maps/search/?api=1&query=${center.lat},${center.lng}`;
    window.open(url, '_blank');
  };

  return isLoaded ? (
    <GoogleMap
      mapContainerStyle={{
        height,
        width: '100%',
        borderRadius: 8,
        position: 'relative',
        ...props.mapContainerStyle,
      }}
      center={center}
      zoom={defaultZoom}
      onLoad={onLoad}
      onClick={onClick}
      onUnmount={onUnmount}
      options={{
        streetViewControl: false,
        mapTypeControl: false,
        gestureHandling: 'greedy',
        zoomControl: true,
        fullscreenControl: !showDirection,
        styles: [
          {
            featureType: 'poi',
            stylers: [
              { visibility: 'off' },
            ],
          },
        ],
        ...props,
      }}
    >
      {children}
      {showDirection && (
        <Button
          onClick={openInGoogleMaps}
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
          }}
        >
          <Icon name="send-outlined" size={20} />
          <Typography variant="label-md" ml={1}>Direction</Typography>
        </Button>
      )}
    </GoogleMap>
  ) : <MapSkeleton height={height} />;
}

const Map = React.memo(BaseMap);

Map.FixedMarker = function (props) {
  return (
    <Icon
      name="location-filled"
      {...props}
      sx={{
        position: 'absolute',
        left: 'calc( 50% - 30px)',
        top: 'calc( 50% - 55px)',
        fontSize: 60,
        lineHeight: 1,
        color: '#000',
        ...props.sx,
      }}
    />
  );
};

Map.Marker = function ({ size = 64, color, ...props }) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const theme = useTheme();
  const svgIcon = `<svg width="49" height="48" viewBox="0 0 49 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M24.6627 4.5C33.988 4.5 41.6758 11.9358 41.6758 21.1355C41.6758 25.7714 39.9898 30.0753 37.2148 33.7232C34.1534 37.747 30.3801 41.2529 26.1329 44.0049C25.1608 44.6408 24.2835 44.6888 23.2167 44.0049C18.9453 41.2529 15.172 37.747 12.1368 33.7232C9.35975 30.0753 7.67578 25.7714 7.67578 21.1355C7.67578 11.9358 15.3635 4.5 24.6627 4.5ZM24.6627 16.0537C21.5791 16.0537 19.0642 18.5956 19.0642 21.6535C19.0642 24.7354 21.5791 27.1593 24.6627 27.1593C27.7483 27.1593 30.2873 24.7354 30.2873 21.6535C30.2873 18.5956 27.7483 16.0537 24.6627 16.0537Z" fill="${color || theme.palette.primary.main}"/></svg>`;
  const customMarkerIcon = {
    url: `data:image/svg+xml;base64,${btoa(svgIcon)}`,
    scaledSize: new window.google.maps.Size(size, size),
  };

  return <MarkerF icon={customMarkerIcon} {...props} />;
};

function MapSkeleton({ height }) {
  return (
    <Skeleton sx={{ height }}>
      <Box
        data-has-skeleton
        sx={{
          height: '100%',
          borderRadius: 8,
        }}
      />
    </Skeleton>
  );
}
export default Map;
